// 浏览器工具客户端(MCP 风格)。宿主浏览器通过 window.rainseeBrowser 暴露
// listTools / callTool,协议见 rainsee-pc md/ai-browser-tools.md。
// 本模块只做"清单获取 + 调用 + 结果转模型可读文本",与具体端(PC/安卓/iOS)无关。

export const BROWSER_TOOL_PREFIX = 'browser_';
// 每轮(一次用户提问)浏览器工具调用上限,防止模型陷入循环。
export const BROWSER_TOOL_BUDGET = 12;

let manifestPromise = null;

const getBridge = () =>
  typeof window !== 'undefined' &&
  window.rainseeBrowser &&
  typeof window.rainseeBrowser.callTool === 'function'
    ? window.rainseeBrowser
    : null;

export const isBrowserToolsAvailable = () => !!getBridge();

export const isBrowserTool = (name) =>
  typeof name === 'string' && name.indexOf(BROWSER_TOOL_PREFIX) === 0;

/** 下发给后端的工具清单(OpenAI function 格式),过滤掉仅供 UI 调用的工具。 */
export const getBrowserToolManifest = () => {
  const bridge = getBridge();
  if (!bridge || typeof bridge.listTools !== 'function') return Promise.resolve([]);
  if (!manifestPromise) {
    manifestPromise = Promise.resolve(bridge.listTools())
      .then((res) => {
        const tools = res && Array.isArray(res.tools) ? res.tools : [];
        return tools
          .filter((tool) => tool && tool.exposeToModel !== false && isBrowserTool(tool.name))
          .map((tool) => ({
            name: tool.name,
            description: tool.description,
            parameters: tool.inputSchema || { type: 'object', properties: {} },
            risk: tool.risk
          }));
      })
      .catch((error) => {
        console.warn('[browserTools] listTools failed:', error);
        manifestPromise = null;
        return [];
      });
  }
  return manifestPromise;
};

export const callBrowserTool = async (name, args = {}) => {
  const bridge = getBridge();
  if (!bridge) {
    return { content: [{ type: 'text', text: '[not_available] Browser tools are unavailable' }], isError: true };
  }
  const result = await bridge.callTool(name, args || {});
  if (!result || !Array.isArray(result.content)) {
    return { content: [{ type: 'text', text: '[failed] Empty tool result' }], isError: true };
  }
  return result;
};

/** 把 ToolResult 拆成 tool 消息文本 + 需要作为图片附件回传的截图。 */
export const splitBrowserToolResult = (result) => {
  const texts = [];
  const images = [];
  for (const item of (result && result.content) || []) {
    if (!item) continue;
    if (item.type === 'text') texts.push(item.text || '');
    if (item.type === 'image' && item.data) {
      images.push({
        type: 'image',
        url: `data:${item.mimeType || 'image/png'};base64,${item.data}`,
        name: 'browser-screenshot'
      });
    }
  }
  if (images.length) texts.push(`[${images.length} screenshot(s) attached to the next message]`);
  return { text: texts.join('\n'), images, isError: !!(result && result.isError) };
};

const TITLES = {
  browser_chrome_inspect: '读取浏览器框架结构',
  browser_chrome_screenshot: '截取浏览器框架',
  browser_chrome_css_get: '读取当前框架 CSS',
  browser_chrome_css_preview: '预览框架样式',
  browser_chrome_css_revert: '撤销样式预览',
  browser_chrome_css_undo: '撤销上次样式修改',
  browser_tabs_list: '查看标签页',
  browser_tab_open: '打开新标签页',
  browser_tab_activate: '切换标签页',
  browser_tab_close: '关闭标签页',
  browser_navigate: '网页导航',
  browser_page_read: '读取网页内容',
  browser_page_selection: '读取选中文本',
  browser_page_screenshot: '截取网页',
  browser_page_run_script: '在网页中执行脚本',
  browser_bookmarks_search: '搜索书签',
  browser_bookmark_add: '添加书签',
  browser_bookmark_remove: '删除书签',
  browser_history_search: '搜索历史记录',
  browser_settings_get: '读取浏览器设置',
  browser_settings_set: '修改浏览器设置',
  browser_newtab_widget_deliver: '发送组件到新标签页预览'
};

export const browserToolStepTitle = (name, args = {}) => {
  const base = TITLES[name] || '操作浏览器';
  if (name === 'browser_chrome_css_preview' && args.summary) return `${base}：${String(args.summary).slice(0, 40)}`;
  if (name === 'browser_chrome_inspect' && args.query) return `${base}：“${String(args.query).slice(0, 24)}”`;
  if ((name === 'browser_tab_open' || name === 'browser_navigate') && args.url) return `${base}：${String(args.url).slice(0, 48)}`;
  return base;
};

export const browserToolStepSummary = (name, text, isError) => {
  if (isError) return String(text || '浏览器工具执行失败').slice(0, 120);
  if (name === 'browser_chrome_css_preview') return '已在浏览器上实时预览，确认后保存';
  if (name === 'browser_chrome_inspect') {
    try { return `找到 ${(JSON.parse(text).nodes || []).length} 个界面元素`; } catch (_) { return '已读取'; }
  }
  return '已完成';
};
